import React from 'react'
import { FormControl, FormHelperText, Skeleton } from '@mui/material'
import { Controller, Control } from 'react-hook-form'
import { Editor } from '@tinymce/tinymce-react'
import useStyles from './styles'
import { useTranslation } from '@contexts/translation'

export const PAGEBREAK_SEPARATOR_API =
  /<div style="page-break-after:always;">[\s\S]*?<\/div>/g // we get everything that's between the divs to replace it
export const PAGEBREAK_SEPARATOR_API_SPACE =
  /<div style="page-break-after: always;">[\s\S]*?<\/div>/g

export const PAGEBREAK_SEPARATOR_INPUT =
  '<div style="page-break-after:always;"><span style="display:none;">&nbsp;</span></div>'

export const PAGEBREAK_SEPARATOR_TINYMCE = '<!-- pagebreak -->'

interface ReactHookFormEditorProps {
  name: string
  control: Control<any>
  errorMessage?: string
  fullWidth?: boolean
  image?: boolean
  height?: number
  fullscreen?: boolean
  pagebreak?: boolean
  loading?: boolean
  contentStyle?: string
  [key: string]: any
}

const ReactHookFormEditor: React.FC<ReactHookFormEditorProps> = ({
  name,
  control,
  errorMessage,
  fullWidth = true,
  image,
  height = 250,
  fullscreen = false,
  pagebreak = false,
  loading = false,
  contentStyle = '',
  ...props
}) => {
  const { dictionary, lang } = useTranslation()
  const classes = useStyles()
  const isError = Boolean(errorMessage)

  const languageCodes: Record<string, string> = {
    fr: 'fr_FR',
    en: 'en_GB',
    es: 'es',
    it: 'it',
    de: 'de',
    nl: 'nl',
    pt: 'pt_PT'
  }

  return !loading ? (
    <FormControl fullWidth={fullWidth} margin="dense">
      <Controller
        name={name}
        control={control}
        render={({ field: { onChange, value } }) => {
          const handleEditorChange = (content: string) => onChange(content)

          return (
            <Editor
              apiKey={process.env.NEXT_PUBLIC_TINY_MCE_KEY}
              value={value}
              onEditorChange={handleEditorChange}
              init={{
                language: languageCodes[lang],
                language_url: `/tinymce/langs/${languageCodes[lang]}.js`,
                height: height,
                menubar: false,
                font_size_formats:
                  '2px 4px 6px 8px 10px 12px 14px 16px 18px 20px 22px 24px 26px 28px 30px 32px 34px 36px',
                plugins: [
                  'code',
                  'advlist',
                  'autolink',
                  'lists',
                  'link',
                  'charmap',
                  'preview',
                  'anchor',
                  'searchreplace',
                  'visualblocks',
                  'code',
                  'insertdatetime',
                  'media',
                  'table',
                  'wordcount',
                  'fullscreen',
                  'pagebreak'
                ],
                toolbar: `undo redo | code | ${
                  fullscreen ? 'fullscreen' : ''
                } | bold italic underline | fontsize | table | \
                      alignleft aligncenter alignright alignjustify | \
                      lineheight bullist numlist outdent indent ${
                        pagebreak ? 'pagebreak' : ''
                      } | forecolor backcolor | ${image ? 'image' : ''}`,
                theme_advanced_resizing: true,
                branding: false,
                table_use_colgroups: false,
                table_class_list: [
                  {
                    title: dictionary.default as string,
                    value: 'tinymce-table-default'
                  },
                  {
                    title: dictionary.tinymce_no_borders as string,
                    value: 'tinymce-table-no-borders'
                  },
                  {
                    title: dictionary.tinymce_table_outline as string,
                    value: 'tinymce-table-outline'
                  },
                  {
                    title: dictionary.tinymce_border_top_bottom as string,
                    value: 'tinymce-table-top-bottom'
                  }
                ],
                line_height_formats:
                  '0.1 0.2 0.3 0.4 0.5 0.6 0.7 0.8 0.9 1 1.1 1.2 1.3 1.4 1.5 1.6 1.7 1.8 1.9 2',
                content_css: '/tinymce/styles/table.css',
                elementpath: false,
                body_class: classes.editor,
                pagebreak_split_block: true,
                content_style:
                  contentStyle ||
                  'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }'
              }}
              {...props}
            />
          )
        }}
      />
      {isError && <FormHelperText error>{errorMessage}</FormHelperText>}
    </FormControl>
  ) : (
    <Skeleton width="100%" height={`${height}px`} variant="rectangular">
      <div />
    </Skeleton>
  )
}

export default ReactHookFormEditor
